import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Open agent conversations, held above the page rather than inside it.
 *
 * A modal was the wrong shape: talking to an agent is something you do *while*
 * looking at a ticket or a pipeline, not instead of it. Keeping the state here,
 * above the router, is what lets a conversation survive navigating away — the
 * thing that makes it usable at all.
 */
const ChatDockContext = createContext({
  chats: [],
  openChat: () => {},
  closeChat: () => {},
  toggleMinimise: () => {},
});

const MAX_OPEN = 3;   // beyond this the dock stops being a dock and becomes a wall

export function ChatDockProvider({ children }) {
  const [chats, setChats] = useState([]);

  const openChat = useCallback((chat) => {
    setChats(prev => {
      const existing = prev.find(c => c.id === chat.id);
      // Re-opening a conversation should surface it, never start a second copy
      // of the same one — the history lives server-side and duplicating the
      // window would show the same thread twice.
      if (existing) {
        return prev.map(c => c.id === chat.id ? { ...c, minimised: false } : c);
      }
      const next = [...prev, { ...chat, minimised: false }];
      return next.length > MAX_OPEN ? next.slice(next.length - MAX_OPEN) : next;
    });
  }, []);

  const closeChat = useCallback((id) => {
    setChats(prev => prev.filter(c => c.id !== id));
  }, []);

  const toggleMinimise = useCallback((id) => {
    setChats(prev => prev.map(c => c.id === id ? { ...c, minimised: !c.minimised } : c));
  }, []);

  return (
    <ChatDockContext.Provider value={{ chats, openChat, closeChat, toggleMinimise }}>
      {children}
    </ChatDockContext.Provider>
  );
}

export function useChatDock() {
  return useContext(ChatDockContext);
}
